import { useState, useMemo, useEffect } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  LinearProgress,
  Snackbar,
} from '@mui/material'
import { Edit as EditIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { dicionarioThorApi } from '../api/dicionariothor'
import type { DicionarioThorRecord, DicionarioThorUpdatePayload } from '../api/dicionariothor'
import ExportButton from '../components/ExportButton'
import ActionBar from '../components/ActionBar'

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100]

const useDebouncedValue = <T,>(value: T, delay = 400): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(handler)
  }, [value, delay])

  return debouncedValue
}

const DicionarioThorPage: React.FC = () => {
  // Memoizar colunas do ExportButton para evitar re-renders
  const exportColumns = useMemo(() => [
    { key: 'nmtabela', label: 'Tabela' },
    { key: 'nmcoluna', label: 'Coluna' },
    { key: 'tpcoluna', label: 'Tipo' },
    { key: 'qtsize', label: 'Tamanho' },
    { key: 'qtdecimal', label: 'Decimais' },
    { key: 'status', label: 'Status' },
    { key: 'dscoluna', label: 'Descrição' },
  ], [])
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [tableFilter, setTableFilter] = useState('')
  const [columnFilter, setColumnFilter] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [sortField, setSortField] = useState<keyof DicionarioThorRecord | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<DicionarioThorRecord | null>(null)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const debouncedSearch = useDebouncedValue(search)
  const debouncedTable = useDebouncedValue(tableFilter)
  const debouncedColumn = useDebouncedValue(columnFilter)

  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useQuery<DicionarioThorRecord[]>({
    queryKey: [
      'dicionariothor',
      {
        search: debouncedSearch,
        tabela: debouncedTable,
        coluna: debouncedColumn,
      },
    ],
    queryFn: () =>
      dicionarioThorApi.list({
        limit: 2000,
        texto: debouncedSearch || undefined,
        tabela: debouncedTable || undefined,
        coluna: debouncedColumn || undefined,
      }),
    staleTime: 60_000,
  })

  const records = data ?? []

  const tableOptions = useMemo(() => {
    const unique = new Set<string>()
    records.forEach((item) => {
      if (item.nmtabela) {
        unique.add(item.nmtabela)
      }
    })
    return Array.from(unique).sort((a, b) => a.localeCompare(b))
  }, [records])

  const columnOptions = useMemo(() => {
    const unique = new Set<string>()
    records
      .filter((item) => {
        if (!debouncedTable) return true
        return item.nmtabela
          ?.toLowerCase()
          .includes(debouncedTable.trim().toLowerCase())
      })
      .forEach((item) => {
        if (item.nmcoluna) {
          unique.add(item.nmcoluna)
        }
      })
    return Array.from(unique).sort((a, b) => a.localeCompare(b))
  }, [records, debouncedTable])

  const filteredData = useMemo((): DicionarioThorRecord[] => {
    if (!debouncedSearch) return records
    const term = debouncedSearch.trim().toLowerCase()

    return records.filter((item) => {
      const values = [
        item.nmtabela,
        item.nmcoluna,
        item.tpcoluna,
        item.nmlabel,
        item.nmheader,
        item.dscoluna,
        item.dsnome,
        item.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return values.includes(term)
    })
  }, [records, debouncedSearch])

  const sortedData = useMemo((): DicionarioThorRecord[] => {
    if (!sortField || !sortDirection) {
      return filteredData
    }

    const multiplier = sortDirection === 'asc' ? 1 : -1

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortField]
      const bValue = b[sortField]

      if (aValue == null && bValue == null) return 0
      if (aValue == null) return -1 * multiplier
      if (bValue == null) return 1 * multiplier

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * multiplier
      }

      return String(aValue).localeCompare(String(bValue), undefined, {
        sensitivity: 'base',
        numeric: true,
      }) * multiplier
    })
  }, [filteredData, sortField, sortDirection])

  const paginated = useMemo((): DicionarioThorRecord[] => {
    const start = page * rowsPerPage
    return sortedData.slice(start, start + rowsPerPage)
  }, [sortedData, page, rowsPerPage])

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: DicionarioThorUpdatePayload }) =>
      dicionarioThorApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dicionariothor'] })
      setSnackbar({ open: true, message: t('common.successUpdate'), severity: 'success' })
      setFormOpen(false)
      setEditingRecord(null)
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || t('common.errorUpdate'),
        severity: 'error',
      })
    },
  })

  const handleOpenForm = (record: DicionarioThorRecord) => {
    setEditingRecord(record)
    setFormOpen(true)
  }

  const handleCloseForm = () => {
    setFormOpen(false)
    setEditingRecord(null)
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!editingRecord) return

    const formData = new FormData(event.currentTarget)
    const payload: DicionarioThorUpdatePayload = {
      nmlabel: (formData.get('nmlabel') as string) || null,
      nmheader: (formData.get('nmheader') as string) || null,
      dscoluna: (formData.get('dscoluna') as string) || null,
      dsnome: (formData.get('dsnome') as string) || null,
      status: (formData.get('status') as string) || null,
    }

    updateMutation.mutate({ id: editingRecord.cndicionariothor, payload })
  }

  const handleSort = (field: keyof DicionarioThorRecord) => {
    if (sortField !== field) {
      setSortField(field)
      setSortDirection('asc')
      setPage(0)
      return
    }

    if (sortDirection === 'asc') {
      setSortDirection('desc')
    } else if (sortDirection === 'desc') {
      setSortDirection(null)
      setSortField(null)
    } else {
      setSortDirection('asc')
    }
    setPage(0)
  }

  const columns: Array<{
    key: keyof DicionarioThorRecord | 'actions'
    label: string
    align?: 'left' | 'right'
  }> = [
    { key: 'nmtabela', label: t('config.dicionarioThor.table') },
    { key: 'nmcoluna', label: t('config.dicionarioThor.column') },
    { key: 'tpcoluna', label: t('config.dicionarioThor.type') },
    { key: 'qtsize', label: t('config.dicionarioThor.length'), align: 'right' },
    { key: 'qtdecimal', label: t('config.dicionarioThor.decimals'), align: 'right' },
    { key: 'status', label: t('config.dicionarioThor.statusColumn') },
                { key: 'dscoluna', label: t('config.dicionarioThor.description') },
    { key: 'actions', label: t('common.actions'), align: 'right' },
  ]

  if (error) {
    return (
      <Box sx={{ width: '100%', py: 4, px: { xs: 2, md: 3 } }}>
        <Alert severity="error">
          {t('config.dicionarioThor.error', 'Erro ao carregar dados do dicionário.')}
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', py: 4, px: { xs: 2, md: 3 } }}>
      <Box display="flex" flexDirection="column" gap={3}>
        <ActionBar>
          <ExportButton
            data={sortedData}
            columns={exportColumns}
            filename="dicionario_thor.xlsx"
            sheetName="Dicionário Thor"
          />
        </ActionBar>

        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }}
          gap={2}
        >
          <Autocomplete
            freeSolo
            options={tableOptions}
            value={tableFilter}
            onChange={(_, newValue) => {
              setTableFilter(newValue ?? '')
              setPage(0)
            }}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === 'input') {
                setTableFilter(newInputValue)
                setPage(0)
              }
              if (reason === 'clear') {
                setTableFilter('')
                setPage(0)
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('config.dicionarioThor.tableFilter')}
                placeholder={t('config.dicionarioThor.tablePlaceholder')}
                fullWidth
              />
            )}
            noOptionsText={t('common.noResults')}
          />
          <Autocomplete
            freeSolo
            options={columnOptions}
            value={columnFilter}
            onChange={(_, newValue) => {
              setColumnFilter(newValue ?? '')
              setPage(0)
            }}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === 'input') {
                setColumnFilter(newInputValue)
                setPage(0)
              }
              if (reason === 'clear') {
                setColumnFilter('')
                setPage(0)
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('config.dicionarioThor.columnFilter')}
                placeholder={t('config.dicionarioThor.columnPlaceholder')}
                fullWidth
              />
            )}
            noOptionsText={t('common.noResults')}
          />
          <TextField
            label={t('common.search')}
            placeholder={t(
              'config.dicionarioThor.searchPlaceholder',
              'Buscar por tabela, coluna ou descrição...'
            )}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(0)
            }}
            fullWidth
          />
        </Box>

        <Paper>
          {(isLoading || isFetching) && <LinearProgress />}
          <TableContainer sx={{ maxHeight: 560 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column) => {
                    const isActive = sortField === column.key && sortDirection !== null
                    return (
                      <TableCell
                        key={column.key}
                        align={column.align ?? 'left'}
                        sortDirection={isActive ? sortDirection ?? 'asc' : false}
                      >
                        <TableSortLabel
                          active={isActive}
                          direction={isActive && sortDirection ? sortDirection : 'asc'}
                          onClick={() => {
                            if (column.key !== 'actions') {
                              handleSort(column.key as keyof DicionarioThorRecord)
                            }
                          }}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    )
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2">
                        {t('common.loading')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary">
                        {t('config.dicionarioThor.empty', 'Nenhum registro encontrado.')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((item) => (
                    <TableRow key={item.cndicionariothor} hover>
                      <TableCell>{item.nmtabela}</TableCell>
                      <TableCell>{item.nmcoluna}</TableCell>
                      <TableCell>{item.tpcoluna ?? '-'}</TableCell>
                  <TableCell align="right">{item.qtsize ?? '-'}</TableCell>
                  <TableCell align="right">{item.qtdecimal ?? '-'}</TableCell>
                  <TableCell>{item.status}</TableCell>
                  <TableCell sx={{ minWidth: 240 }}>
                    {item.dscoluna ?? '-'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenForm(item)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            count={sortedData.length}
            labelRowsPerPage={t('common.rowsPerPage')}
          />
        </Paper>
      </Box>

      {/* Dialog de edição */}
      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {t('config.dicionarioThor.editRecord', 'Editar Registro')}
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 2 }}>
              <TextField
                label={t('config.dicionarioThor.table')}
                value={editingRecord?.nmtabela || ''}
                fullWidth
                disabled
              />
              <TextField
                label={t('config.dicionarioThor.column')}
                value={editingRecord?.nmcoluna || ''}
                fullWidth
                disabled
              />
              <TextField
                name="nmlabel"
                label={t('config.dicionarioThor.label', 'Label')}
                defaultValue={editingRecord?.nmlabel || ''}
                fullWidth
              />
              <TextField
                name="nmheader"
                label={t('config.dicionarioThor.header', 'Header')}
                defaultValue={editingRecord?.nmheader || ''}
                fullWidth
              />
              <TextField
                name="dscoluna"
                label={t('config.dicionarioThor.description')}
                defaultValue={editingRecord?.dscoluna || ''}
                fullWidth
                multiline
                rows={3}
              />
              <TextField
                name="dsnome"
                label={t('config.dicionarioThor.nameDescription', 'Descrição do Nome')}
                defaultValue={editingRecord?.dsnome || ''}
                fullWidth
                multiline
                rows={2}
              />
              <TextField
                name="status"
                label={t('config.dicionarioThor.statusColumn')}
                defaultValue={editingRecord?.status || ''}
                fullWidth
                inputProps={{ maxLength: 1 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForm}>{t('common.cancel')}</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? t('common.loading') : t('common.save')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default DicionarioThorPage


